/* KST 3-Part Protocol PDP — shared interactive behaviour.

   Reimplements, in vanilla JS, the interactivity the Claude Design export
   expressed through its component logic:
     - product tabs (science section)
     - ingredients / research modals
     - mobile sticky bar (shows past 520px of scroll, hides while a modal is open)
     - the offer product carousel that replaces the single offer image

   All click handling runs on `window` in the CAPTURE phase: the theme
   (Shrine Pro) attaches its own document-level click handlers that otherwise
   swallow clicks on buttons wrapping images. Reduced motion is honored. */
(function () {
  if (window.__kpUiInit) return;
  window.__kpUiInit = true;

  var openModal = null;

  function prefersReduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------- Product tabs ---------- */
  function selectTab(tab) {
    var scope = tab.closest('[data-kp-tabs]');
    if (!scope) return;
    var index = tab.getAttribute('data-kp-tab');
    var tabs = scope.querySelectorAll('[data-kp-tab]');
    var panels = scope.querySelectorAll('[data-kp-panel]');
    for (var i = 0; i < tabs.length; i++) {
      var isMatch = tabs[i].getAttribute('data-kp-tab') === index;
      tabs[i].setAttribute('aria-selected', isMatch ? 'true' : 'false');
      tabs[i].setAttribute('tabindex', isMatch ? '0' : '-1');
    }
    for (var j = 0; j < panels.length; j++) {
      panels[j].hidden = panels[j].getAttribute('data-kp-panel') !== index;
    }
  }

  /* ---------- Modals ---------- */
  function showModal(id) {
    var modal = document.getElementById(id);
    if (!modal) return;
    modal.classList.add('is-open');
    modal.removeAttribute('hidden');
    document.documentElement.style.overflow = 'hidden';
    openModal = modal;
    var focusable = modal.querySelector('[data-kp-modal-close]');
    if (focusable) focusable.focus();
    // The sticky bar must not sit on top of an open modal.
    var bar = document.querySelector('[data-kp-sticky]');
    if (bar) bar.classList.remove('is-visible');
  }

  function hideModal(modal) {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('hidden', '');
    document.documentElement.style.overflow = '';
    if (openModal === modal) openModal = null;
    syncSticky();
  }

  /* ---------- Sticky bar ---------- */
  function syncSticky() {
    var bar = document.querySelector('[data-kp-sticky]');
    if (!bar) return;
    var threshold = parseInt(bar.getAttribute('data-kp-threshold') || '520', 10);
    var shouldShow = window.scrollY > threshold && !openModal;
    bar.classList.toggle('is-visible', shouldShow);
    bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
  }

  /* ---------- Offer carousel ---------- */
  function carouselParts(root) {
    return {
      stage: root.querySelector('[data-kp-stage]'),
      slides: Array.prototype.slice.call(root.querySelectorAll('[data-kp-slide]')),
      thumbs: Array.prototype.slice.call(root.querySelectorAll('[data-kp-thumb]')),
    };
  }

  function setCarouselActive(thumbs, index) {
    for (var i = 0; i < thumbs.length; i++) {
      thumbs[i].setAttribute('aria-current', i === index ? 'true' : 'false');
    }
  }

  function wireCarousel(root) {
    if (root.__kpWired) return;
    root.__kpWired = true;
    var p = carouselParts(root);
    if (!p.stage || !p.slides.length) return;

    var timer;
    p.stage.addEventListener(
      'scroll',
      function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          var center = p.stage.scrollLeft + p.stage.clientWidth / 2;
          var best = 0;
          var bestDistance = Infinity;
          for (var i = 0; i < p.slides.length; i++) {
            var slide = p.slides[i];
            var mid = slide.offsetLeft - p.stage.offsetLeft + slide.clientWidth / 2;
            var d = Math.abs(mid - center);
            if (d < bestDistance) { bestDistance = d; best = i; }
          }
          setCarouselActive(p.thumbs, best);
        }, 80);
      },
      { passive: true }
    );
  }

  /* ---------- Delegated clicks (capture phase) ---------- */
  window.addEventListener(
    'click',
    function (event) {
      var tab = event.target.closest('[data-kp-tab]');
      if (tab) {
        event.preventDefault();
        event.stopPropagation();
        selectTab(tab);
        return;
      }

      var opener = event.target.closest('[data-kp-modal-open]');
      if (opener) {
        event.preventDefault();
        event.stopPropagation();
        showModal(opener.getAttribute('data-kp-modal-open'));
        return;
      }

      var closer = event.target.closest('[data-kp-modal-close]');
      if (closer) {
        event.preventDefault();
        event.stopPropagation();
        hideModal(closer.closest('[data-kp-modal]'));
        return;
      }

      var backdrop = event.target.closest('[data-kp-modal]');
      if (backdrop && event.target === backdrop) {
        hideModal(backdrop);
        return;
      }

      var thumb = event.target.closest('[data-kp-thumb]');
      if (thumb) {
        event.preventDefault();
        event.stopPropagation();
        var root = thumb.closest('[data-kp-carousel]');
        if (!root) return;
        var p = carouselParts(root);
        var index = p.thumbs.indexOf(thumb);
        var target = p.slides[index];
        if (p.stage && target) {
          p.stage.scrollTo({
            left: target.offsetLeft - p.stage.offsetLeft,
            behavior: prefersReduced() ? 'auto' : 'smooth',
          });
        }
        setCarouselActive(p.thumbs, index);
      }
    },
    true
  );

  window.addEventListener(
    'keydown',
    function (event) {
      if (event.key === 'Escape' && openModal) hideModal(openModal);
    },
    true
  );

  window.addEventListener('scroll', syncSticky, { passive: true });

  function init(scope) {
    var root = scope || document;
    var carousels = root.querySelectorAll('[data-kp-carousel]');
    for (var i = 0; i < carousels.length; i++) wireCarousel(carousels[i]);
    syncSticky();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
  } else {
    init();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
